import React, { CSSProperties, useState } from 'react';
import SecurityIcon from '@mui/icons-material/Security';
import GameModal, { primaryBtnStyle, secondaryBtnStyle, btnRowStyle } from 'components/GameModal/GameModal';

interface Question {
  QuestionId: number;
  Text: { Statement: string };
  Choices: Array<{
    ChoiceId: number;
    Text: { Statement: string };
  }>;
}

interface KYCQuestionSet {
  QuestionSetId: number;
  Questions: Question[];
}

type KYCModalProps = {
  open: boolean;
  questions: KYCQuestionSet | null;
  onSubmit: (answers: any) => Promise<void>;
  onClose: () => void;
  loading?: boolean;
};

const KYCModal = ({ open, questions, onSubmit, onClose, loading = false }: KYCModalProps) => {
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, string>>({});
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSelect = (questionId: number, choiceId: string) => {
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: choiceId }));
    setValidationError(null);
  };

  const handleSubmit = async () => {
    if (!questions) return;

    if (Object.keys(selectedAnswers).length < questions.Questions.length) {
      setValidationError('Please answer all questions before submitting.');
      return;
    }

    const formattedAnswers = {
      QuestionSetId: questions.QuestionSetId,
      Questions: questions.Questions.map((q) => ({
        QuestionId: q.QuestionId,
        Choices: [{ Choice: selectedAnswers[q.QuestionId] }],
      })),
    };

    await onSubmit(formattedAnswers);
  };

  const handleClose = () => {
    setSelectedAnswers({});
    setValidationError(null);
    onClose();
  };

  if (!questions) return null;

  return (
    <GameModal
      open={open}
      onClose={handleClose}
      title="Identity Check"
      maxWidth="460px"
      closeOnBackdropClick={false}
      // Render contained within the cashier sidebar (absolute, no portal-to-body)
      // so the questionnaire stays inside the panel as it did pre-merge — the
      // portaled overlay (zIndex 200) renders behind the cashier panel (zIndex 950).
      inline
    >
      <div style={contentStyle}>
        <div style={headerInfo}>
          <SecurityIcon style={{ fontSize: '1.5rem', color: '#E98C26' }} />
          <span style={headerDesc}>
            Please answer the following questions to complete your identity check. This is required before you can proceed.
          </span>
        </div>

        {/* Questions */}
        {questions.Questions.map((question, index) => (
          <div key={question.QuestionId} style={questionBlock}>
            <span style={questionText}>{index + 1}. {question.Text.Statement}</span>
            <div style={choicesCol}>
              {question.Choices.map((choice) => {
                const isSelected = selectedAnswers[question.QuestionId] === String(choice.ChoiceId);
                return (
                  <button
                    key={choice.ChoiceId}
                    onClick={() => handleSelect(question.QuestionId, String(choice.ChoiceId))}
                    style={{ ...choiceBtn, ...(isSelected ? choiceBtnActive : {}) }}
                  >
                    <div style={{ ...radioCircle, ...(isSelected ? radioCircleActive : {}) }}>
                      {isSelected && <div style={radioDot} />}
                    </div>
                    <span style={choiceText}>{choice.Text.Statement}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {validationError && <div style={errorBox}>{validationError}</div>}

        <div style={btnRowStyle}>
          <button style={secondaryBtnStyle} onClick={handleClose} disabled={loading}>
            Cancel
          </button>
          <button
            style={{ ...primaryBtnStyle, ...(loading ? { opacity: 0.6 } : {}) }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Verifying...' : 'Submit Answers'}
          </button>
        </div>
      </div>
    </GameModal>
  );
};

export default KYCModal;

/* ── Styles ── */

const contentStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px'
};

const headerInfo: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px'
};

const headerDesc: CSSProperties = {
  fontSize: '0.8rem',
  color: 'rgba(255,255,255,0.6)',
  lineHeight: 1.5
};

const questionBlock: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
};

const questionText: CSSProperties = {
  fontSize: '0.85rem',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.9)',
  lineHeight: 1.4
};

const choicesCol: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  paddingLeft: '8px'
};

const choiceBtn: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '10px 12px',
  backgroundColor: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '10px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  textAlign: 'left',
  transition: 'all 0.15s ease'
};

const choiceBtnActive: CSSProperties = {
  backgroundColor: 'rgba(233, 140, 38, 0.1)',
  borderColor: 'rgba(233, 140, 38, 0.3)'
};

const radioCircle: CSSProperties = {
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: '2px solid rgba(255,255,255,0.2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
};

const radioCircleActive: CSSProperties = {
  borderColor: '#E98C26'
};

const radioDot: CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: '#E98C26'
};

const choiceText: CSSProperties = {
  fontSize: '0.8rem',
  color: 'rgba(255,255,255,0.75)'
};

const errorBox: CSSProperties = {
  fontSize: '0.75rem',
  color: '#e74c3c',
  fontWeight: 600,
  padding: '8px 12px',
  backgroundColor: 'rgba(231, 76, 60, 0.1)',
  border: '1px solid rgba(231, 76, 60, 0.2)',
  borderRadius: '8px'
};
